<template>
  <div
    v-if="isVisible"
    class="quick-edit-shipping-expense-modal-root"
    role="presentation"
  >
    <div class="modal-backdrop fade show" @click.self="close"></div>
    <div
      class="modal fade show"
      tabindex="-1"
      role="dialog"
      @click.self="close"
    >
      <div class="modal-dialog modal-md modal-dialog-centered">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              <i
                class="fas fa-pencil-alt me-2 text-secondary"
                aria-hidden="true"
              ></i>
              {{ $t("financialSummary.stages.editShippingExpenseModalTitle") }}
            </h5>
            <button
              type="button"
              class="btn-close"
              :aria-label="$t('close')"
              @click="close"
            ></button>
          </div>
          <div class="modal-body">
            <ContentSkeleton v-if="loading" variant="form" :rows="4" padded />
            <template v-else-if="loadError">
              <div class="alert alert-danger mb-0">{{ loadError }}</div>
            </template>
            <template v-else>
              <div class="mb-3">
                <label class="form-label">{{ $t("Amount") }}</label>
                <input
                  v-model.number="amountDraft"
                  type="number"
                  step="0.01"
                  :min="amountMinApplied"
                  class="form-control text-end tabular-nums"
                />
                <div v-if="amountFollowsPayment" class="form-text">
                  {{
                    $t(
                      "financialSummary.stages.editShippingExpenseAmountFollowsPaymentHint"
                    )
                  }}
                </div>
                <div v-else-if="amountMinApplied > 0.01" class="form-text">
                  {{
                    $t(
                      "financialSummary.stages.editShippingExpenseAmountLockedHint"
                    )
                  }}
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t("Contact") }}</label>
                <FieldReference
                  :field="expenseContactFieldDef"
                  :value="contactRefValue"
                  mode="edit"
                  field-id="quick-edit-expense-contact"
                  editing-fields="contact"
                  :show-label="false"
                  :popup="true"
                  :reference-options="contactReferenceOptions"
                  disable-lock
                  @input="onContactInput"
                  @update:editing-fields="() => {}"
                  @open-reference-modal="() => {}"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t("Date") }}</label>
                <InputDate
                  :field="dateFieldDef"
                  :value="dateDraft"
                  @input="dateDraft = $event"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t("Invoice") }}</label>
                <FieldFile
                  :field="expenseFileFieldDef"
                  :value="fileRefValue"
                  mode="edit"
                  field-id="quick-edit-expense-file"
                  editing-fields="file"
                  :show-label="false"
                  disable-lock
                  @input="onFileInput"
                  @update:editing-fields="() => {}"
                />
              </div>
              <div class="mb-0">
                <label class="form-label">{{ $t("Comment") }}</label>
                <textarea
                  v-model="commentDraft"
                  class="form-control"
                  rows="3"
                  spellcheck="false"
                ></textarea>
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <small
              v-if="!canDeleteExpense"
              class="text-danger me-auto"
            >
              <i class="fas fa-link me-1" aria-hidden="true"></i>
              {{ $t("financialSummary.stages.editShippingExpenseDeleteBlockedHint") }}
            </small>
            <button
              type="button"
              class="btn btn-outline-danger"
              :disabled="loading || !!loadError || saving || deleting || !canDeleteExpense"
              @click="removeExpense"
            >
              <span
                v-if="deleting"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              {{ $t("Delete") }}
            </button>
            <button type="button" class="btn btn-secondary" @click="close">
              {{ $t("cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="loading || !!loadError || saving || deleting"
              @click="submit"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              {{ $t("Save") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import InputDate from "./inputs/InputDate.vue";
import { FieldReference, FieldFile } from "../model-definition-core";
import {
  fileRefValueFromExpenseDoc,
  serializeExpenseFileForApi,
  fileFieldDefFromShippingDefinition,
} from "@/utils/shippingExpenseFileField";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

function appliedAmountFromDoc(doc) {
  if (!doc || typeof doc !== "object") return 0;
  const n = Number(doc.paid);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Referencias pobladas → solo id para PUT */
function stripPopulatedRefsForApi(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const out = { ...obj };
  for (const k of Object.keys(out)) {
    const v = out[k];
    if (
      v &&
      typeof v === "object" &&
      !Array.isArray(v) &&
      !(v instanceof Date) &&
      v._id != null
    ) {
      out[k] = String(v._id);
    }
  }
  return out;
}

function contactRefValueFromDoc(contact) {
  if (contact == null || contact === "") return null;
  if (typeof contact === "object" && contact._id != null) {
    const name =
      (contact.name && String(contact.name).trim()) || String(contact._id);
    return { _id: contact._id, name };
  }
  const s = String(contact).trim();
  return /^[a-f0-9]{24}$/i.test(s) ? s : null;
}

function contactRefValueToId(val) {
  if (val == null || val === "") return "";
  if (typeof val === "object" && val._id != null) return String(val._id);
  const s = String(val).trim();
  return /^[a-f0-9]{24}$/i.test(s) ? s : "";
}

function resolveCommentKey(doc) {
  if (!doc || typeof doc !== "object") return "comments";
  if (Object.prototype.hasOwnProperty.call(doc, "comment")) return "comment";
  if (Object.prototype.hasOwnProperty.call(doc, "comments")) return "comments";
  return "comments";
}

function readCommentFromDoc(doc, key) {
  if (!doc) return "";
  const v = doc[key];
  if (v == null) return "";
  return String(v);
}

export default {
  name: "QuickEditShippingExpenseModal",
  components: {
    ContentSkeleton,
    InputDate,
    FieldReference,
    FieldFile,
  },
  props: {
    isVisible: { type: Boolean, default: false },
    expenseId: { type: String, default: null },
    /** El pago automático del gasto sigue al importe: se puede bajar de lo aplicado. */
    amountFollowsPayment: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      loadError: "",
      loadedExpense: null,
      amountDraft: null,
      amountMinApplied: 0,
      contactRefValue: null,
      dateDraft: "",
      fileRefValue: null,
      expenseFileFieldDefLoaded: fileFieldDefFromShippingDefinition([]),
      commentDraft: "",
      commentKey: "comments",
      contactReferenceOptions: [],
      dateFieldDef: {
        type: "date",
        key: "date",
        time: false,
      },
    };
  },
  computed: {
    canDeleteExpense() {
      if (!this.loadedExpense) return false;
      const paid = Number(this.loadedExpense.paid);
      if (Number.isFinite(paid) && paid > 0.01) return false;
      const payments = this.loadedExpense.payments;
      return !(Array.isArray(payments) && payments.length > 0);
    },
    expenseContactFieldDef() {
      return {
        type: "reference",
        ref: "contact",
        key: "contact",
        label: "Contact",
        preload: true,
        isArray: false,
        readonly: false,
        safe: false,
        hide: false,
      };
    },
    expenseFileFieldDef() {
      return this.expenseFileFieldDefLoaded;
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(val) {
        if (val && this.expenseId) {
          this.loadExpense();
        }
        if (!val) {
          this.resetLocal();
        }
      },
    },
    expenseId() {
      if (this.isVisible && this.expenseId) {
        this.loadExpense();
      }
    },
  },
  methods: {
    resetLocal() {
      this.loadError = "";
      this.loadedExpense = null;
      this.amountDraft = null;
      this.amountMinApplied = 0;
      this.deleting = false;
      this.contactRefValue = null;
      this.dateDraft = "";
      this.fileRefValue = null;
      this.commentDraft = "";
      this.commentKey = "comments";
    },
    async loadContactReferenceOptions() {
      this.contactReferenceOptions = [];
      try {
        const params = new URLSearchParams();
        params.append("perPage", "1000");
        params.append("fields", "_id,name,label,textPlainVersion");
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/contact?${params.toString()}`
        );
        const items = (data && (data.items || data)) || [];
        this.contactReferenceOptions = Array.isArray(items)
          ? items.filter((x) => x && (x._id || x.name))
          : [];
      } catch (e) {
        console.warn("QuickEditShippingExpenseModal: contacts", e);
        this.contactReferenceOptions = [];
      }
    },
    async loadExpense() {
      if (!this.expenseId) return;
      this.loading = true;
      this.loadError = "";
      try {
        await this.loadContactReferenceOptions();
        try {
          const { data: shipDef } = await wencesApi.get(
            "/api/gotrading/model-definitions/shipping"
          );
          this.expenseFileFieldDefLoaded = fileFieldDefFromShippingDefinition(
            shipDef && shipDef.fields
          );
        } catch (defErr) {
          console.warn("QuickEditShippingExpenseModal: shipping model def", defErr);
        }
        const { data: doc } = await wencesApi.get(
          `/api/gotrading/dynamic/shipping_expenses/${encodeURIComponent(
            String(this.expenseId)
          )}`
        );
        if (!doc || !doc._id) {
          this.loadError = this.$t(
            "financialSummary.stages.editShippingExpenseLoadError"
          );
          return;
        }
        this.loadedExpense = doc;
        this.amountMinApplied = this.amountFollowsPayment ? 0 : appliedAmountFromDoc(doc);
        const amt = Number(doc.amount);
        this.amountDraft = Number.isFinite(amt) ? amt : null;
        this.contactRefValue = contactRefValueFromDoc(doc.contact);
        this.dateDraft =
          typeof doc.date === "string"
            ? doc.date.includes("T")
              ? doc.date.split("T")[0]
              : doc.date
            : "";
        this.fileRefValue = fileRefValueFromExpenseDoc(
          doc.file,
          this.expenseFileFieldDef.isArray
        );
        this.commentKey = resolveCommentKey(doc);
        this.commentDraft = readCommentFromDoc(doc, this.commentKey);
      } catch (e) {
        console.error("QuickEditShippingExpenseModal loadExpense:", e);
        this.loadError =
          (e.response &&
            e.response.data &&
            (e.response.data.message || e.response.data.error)) ||
          e.message ||
          this.$t("financialSummary.stages.editShippingExpenseLoadError");
      } finally {
        this.loading = false;
      }
    },
    onContactInput(v) {
      this.contactRefValue = v;
    },
    onFileInput(v) {
      this.fileRefValue = v;
    },
    close() {
      this.$emit("close");
    },
    async removeExpense() {
      if (!this.loadedExpense || !this.loadedExpense._id || this.deleting) return;
      if (!this.canDeleteExpense) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.stages.editShippingExpenseDeleteBlockedHint"),
        });
        return;
      }
      const result = await Swal.fire({
        icon: "warning",
        title: this.$tn("Delete"),
        text: this.$tn("Are you sure?"),
        showCancelButton: true,
        confirmButtonText: this.$tn("Delete"),
        cancelButtonText: this.$tn("cancel"),
        confirmButtonColor: "#dc3545",
      });
      if (!result.isConfirmed) return;
      this.deleting = true;
      try {
        await wencesApi.delete(
          `/api/gotrading/dynamic/shipping_expenses/${encodeURIComponent(
            String(this.loadedExpense._id)
          )}`
        );
        await Swal.fire({
          icon: "success",
          title: this.$tn("success"),
          timer: 1400,
          showConfirmButton: false,
        });
        this.$emit("saved");
        this.close();
      } catch (e) {
        console.error("QuickEditShippingExpenseModal removeExpense:", e);
        const msg =
          (e.response &&
            e.response.data &&
            (e.response.data.message || e.response.data.error)) ||
          e.message ||
          this.$t("error");
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: typeof msg === "string" ? msg : this.$tn("error"),
        });
      } finally {
        this.deleting = false;
      }
    },
    async submit() {
      if (!this.loadedExpense || !this.loadedExpense._id) return;
      const n = Number(this.amountDraft);
      if (!Number.isFinite(n) || n < 0) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("error"),
          text: this.$tn(
            "financialSummary.stages.editShippingExpenseInvalidAmount"
          ),
        });
        return;
      }
      if (Number.isFinite(this.amountMinApplied) && n + 1e-6 < this.amountMinApplied) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("error"),
          text: this.$tn(
            "financialSummary.stages.editShippingExpenseAmountLockedHint"
          ),
        });
        return;
      }

      this.saving = true;
      try {
        const payload = {
          partialUpdate: true,
          amount: parseFloat(n.toFixed(2)),
        };

        const cid = contactRefValueToId(this.contactRefValue);
        if (cid) {
          payload.contact = cid;
        }

        if (this.dateDraft && String(this.dateDraft).trim()) {
          payload.date = String(this.dateDraft).trim();
        } else {
          payload.date = null;
        }

        const serializedFile = serializeExpenseFileForApi(
          this.fileRefValue,
          this.expenseFileFieldDef.isArray
        );
        if (serializedFile != null) {
          payload.file = serializedFile;
        }

        const ck = this.commentKey;
        payload[ck] =
          this.commentDraft != null ? String(this.commentDraft) : "";

        await wencesApi.put(
          `/api/gotrading/dynamic/shipping_expenses/${encodeURIComponent(
            String(this.loadedExpense._id)
          )}`,
          payload
        );

        await Swal.fire({
          icon: "success",
          title: this.$tn("success"),
          timer: 1600,
          showConfirmButton: false,
        });
        this.$emit("saved");
        this.close();
      } catch (e) {
        console.error("QuickEditShippingExpenseModal submit:", e);
        const msg =
          (e.response &&
            e.response.data &&
            (e.response.data.message || e.response.data.error)) ||
          e.message ||
          this.$t("error");
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: typeof msg === "string" ? msg : this.$tn("error"),
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.quick-edit-shipping-expense-modal-root {
  position: fixed;
  inset: 0;
  z-index: 1055;
}
.quick-edit-shipping-expense-modal-root .modal-backdrop {
  z-index: 1;
}
.quick-edit-shipping-expense-modal-root .modal {
  display: block;
  position: relative;
  z-index: 2;
}
</style>
